/**
 * 로그 화면(§8.1). 앱 로그와 활동 로그(MCP 호출·브리지·세션·저장·파일)를 한 목록으로 본다.
 * 읽는 화면이라 주 행동은 없다. 보조 동작은 ⋯ 메뉴에 둔다.
 */
import { Caption1, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { ArrowClockwise16Regular, MoreHorizontal16Regular, Pause16Regular, Play16Regular } from "@fluentui/react-icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSettingsHub } from "@/shell/settings-hub";
import { ConfirmDialog } from "@/components/app-dialog";
import { FuiButton, FuiFilterSelect } from "@/components/fui-control";
import { GhostEmptyState } from "@/components/ghost-empty-state";
import { ListSearch, ListToolbar } from "@/components/list-toolbar";
import { SegmentedControl } from "@/features/resources/SegmentedControl";
import { shellGutter } from "@/shell/content-frame";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { ConnectionBadge } from "@/desktop/ConnectionBadge";
import catalog from "@/desktop/mcp-tools.json";
import { invoke, isDesktop } from "@/desktop/runtime";
import { useConnectionStatus } from "@/desktop/use-connection-status";
import { LogDetail } from "./LogDetail";
import { LogSettingsDialog } from "./LogSettingsDialog";
import { LogTable } from "./LogTable";
import { EMPTY_FILTER, filterToParams, GROUP_LABEL, hasFilter, LEVEL_LABEL, parseFilter, PERIOD_LABEL, STATUS_LABEL, type LogFilter, type LogGroup } from "./log-model";
import { useActivityLog } from "./use-activity-log";

const useStyles = makeStyles({
  page: { display: "flex", flexDirection: "column", height: "100%", minHeight: 0, backgroundColor: tokens.colorNeutralBackground2 },
  count: { marginLeft: "auto", color: tokens.colorNeutralForeground3, whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" },
  content: { flex: 1, minHeight: 0, display: "flex", flexDirection: "column", gap: tokens.spacingVerticalS, padding: `0 ${shellGutter} ${tokens.spacingVerticalXXL}` },
  notice: { display: "flex", alignItems: "center", gap: tokens.spacingHorizontalS, color: tokens.colorNeutralForeground2 },
  error: { color: tokens.colorStatusDangerForeground1 },
  empty: { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: tokens.spacingVerticalM },
  srOnly: { position: "absolute", width: "1px", height: "1px", overflow: "hidden", clipPath: "inset(50%)", whiteSpace: "nowrap" },
});

const GROUPS: LogGroup[] = ["all", "app", "mcp"];
const TOOL_OPTIONS = [{ value: "", label: "모든 도구" }, ...catalog.tools.map((t) => ({ value: t.name, label: t.name }))];
const LEVEL_OPTIONS = [{ value: "", label: "모든 수준" }, ...(["info", "warn", "error"] as const).map((l) => ({ value: l, label: `${LEVEL_LABEL[l]} 이상` }))];
const STATUS_OPTIONS = [{ value: "", label: "모든 상태" }, ...(Object.keys(STATUS_LABEL) as Array<keyof typeof STATUS_LABEL>).map((s) => ({ value: s, label: STATUS_LABEL[s] }))];
const PERIOD_OPTIONS = (Object.keys(PERIOD_LABEL) as Array<keyof typeof PERIOD_LABEL>).map((p) => ({ value: p, label: PERIOD_LABEL[p] }));

const n = (v: number) => v.toLocaleString("ko-KR");

export function SystemLogsView() {
  const styles = useStyles();
  if (!isDesktop()) {
    return (
      <div className={styles.page}>
        <div className={styles.empty}>
          <GhostEmptyState title="데스크탑 앱에서 볼 수 있어요" description="로그는 XPlanner 데스크탑 앱이 이 기기에 남기는 기록입니다." />
        </div>
      </div>
    );
  }
  return <DesktopLogs />;
}

function DesktopLogs() {
  const styles = useStyles();
  const [params, setParams] = useSearchParams();
  const filter = useMemo(() => parseFilter(params), [params]);
  const [paused, setPaused] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [confirmClear, setConfirmClear] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [live, setLive] = useState("");
  const status = useConnectionStatus();
  // 스크린리더에는 새 항목이 아니라 오류만 알린다(ADV-L03).
  const onErrors = useCallback((count: number) => setLive(`오류 기록 ${count}건이 새로 들어왔습니다`), []);
  const log = useActivityLog(filter, paused, onErrors);

  const setFilter = (patch: Partial<LogFilter>) => setParams(filterToParams({ ...filter, ...patch }));
  // 검색어는 입력이 멈춘 뒤 URL에 싣는다(글자마다 파일을 다시 읽지 않는다).
  const [text, setText] = useState(filter.text);
  useEffect(() => setText(filter.text), [filter.text]);
  useEffect(() => {
    if (text === filter.text) return;
    const t = window.setTimeout(() => setParams(filterToParams({ ...filter, text })), 300);
    return () => window.clearTimeout(t);
  }, [text, filter, setParams]);
  const selected = detailOpen && selectedId ? log.items.find((e) => e.id === selectedId) : undefined;

  const run = async (cmd: string, fail: string) => {
    setActionError(null);
    try {
      await invoke(cmd);
      return true;
    } catch {
      setActionError(fail);
      return false;
    }
  };

  const filtered = hasFilter(filter);
  const showEmpty = !log.loading && !log.error && log.items.length === 0;

  return (
    <div className={styles.page}>

      <ListToolbar
        search={<ListSearch value={text} onChange={setText} />}
        filters={
          <>
            <FuiFilterSelect label="수준" value={filter.minLevel} options={LEVEL_OPTIONS} onValueChange={(v) => setFilter({ minLevel: v as LogFilter["minLevel"] })} />
            <FuiFilterSelect label="상태" value={filter.status} options={STATUS_OPTIONS} onValueChange={(v) => setFilter({ status: v as LogFilter["status"] })} />
            <FuiFilterSelect label="도구" value={filter.action} options={TOOL_OPTIONS} onValueChange={(v) => setFilter({ action: v })} />
            <FuiFilterSelect label="기간" value={filter.period} options={PERIOD_OPTIONS} onValueChange={(v) => setFilter({ period: v as LogFilter["period"] })} />
          </>
        }
        trail={
          <Caption1 className={styles.count} aria-live="off">
            {filtered ? `전체 ${n(log.total)}건 중 ${n(log.matched)}건` : `전체 ${n(log.total)}건`}
            {log.dropped > 0 && ` · 화면에서 ${n(log.dropped)}건 생략`}
            {log.corruptLines > 0 && ` · 읽지 못한 줄 ${n(log.corruptLines)}건`}
          </Caption1>
        }
        view={
          <SegmentedControl
            value={filter.group}
            onChange={(g) => setFilter({ group: g })}
            ariaLabel="로그 종류"
            options={GROUPS.map((g) => ({ value: g, label: GROUP_LABEL[g] }))}
          />
        }
        tools={
          <>
            <ConnectionBadge status={status} />
            <FuiButton appearance="subtle" icon={paused ? <Play16Regular /> : <Pause16Regular />} aria-pressed={paused} onClick={() => setPaused((p) => !p)}>
          {paused ? (log.held > 0 ? `다시 보기 · ${n(log.held)}건 대기` : "다시 보기") : "일시정지"}
            </FuiButton>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <FuiButton appearance="subtle" icon={<MoreHorizontal16Regular />} aria-label="로그 메뉴" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => void run("open_log_folder", "로그 폴더를 열지 못했습니다")}>로그 폴더 열기</DropdownMenuItem>
                <DropdownMenuItem onClick={() => void run("export_activity_log", "로그를 내보내지 못했습니다")}>내보내기…</DropdownMenuItem>
                <DropdownMenuItem onClick={() => setSettingsOpen(true)}>로그 설정…</DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setConfirmClear(true)}>모두 지우기…</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        }
      />

      <div className={styles.content}>
        {!log.enabled && (
          <div className={styles.notice}>
            <Caption1>로그 기록이 꺼져 있어 새 기록을 남기지 않습니다.</Caption1>
            <FuiButton appearance="subtle" onClick={() => setSettingsOpen(true)}>
              설정 열기
            </FuiButton>
          </div>
        )}
        {(log.error || log.writeError || actionError) && (
          <div className={mergeClasses(styles.notice, styles.error)} role="alert">
            <Caption1>{log.error ? "로그를 읽지 못했습니다." : actionError ?? "로그 파일에 쓰지 못하고 있습니다. 폴더 권한을 확인하세요."}</Caption1>
            {log.error && (
              <FuiButton appearance="subtle" icon={<ArrowClockwise16Regular />} onClick={() => void log.reload()}>
                다시 시도
              </FuiButton>
            )}
          </div>
        )}

        {showEmpty ? (
          <div className={styles.empty}>
            {filtered ? (
              <>
                <GhostEmptyState variant="search" title="필터에 맞는 기록이 없어요" description={`전체 ${n(log.total)}건이 필터로 가려져 있습니다.`} />
                <FuiButton appearance="secondary" onClick={() => setParams(filterToParams(EMPTY_FILTER))}>
                  필터 해제
                </FuiButton>
              </>
            ) : (
              <>
                <GhostEmptyState title="아직 기록이 없어요" description="MCP 클라이언트를 연결하면 호출이 여기에 보입니다." />
                <FuiButton appearance="primary" onClick={() => useSettingsHub.getState().open("app", "integration")}>
                  MCP 연결
                </FuiButton>
              </>
            )}
          </div>
        ) : (
          <LogTable
            items={log.items}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onOpenDetail={() => setDetailOpen(true)}
            onCloseDetail={() => setDetailOpen(false)}
            onReachTop={log.nextBefore ? () => void log.loadOlder() : undefined}
          />
        )}
        {selected && <LogDetail entry={selected} onClose={() => setDetailOpen(false)} />}
      </div>

      <span className={styles.srOnly} aria-live="polite">
        {live}
      </span>

      <ConfirmDialog
        open={confirmClear}
        title="로그 지우기"
        message="「로그」 전체를 지울까요? 앱이 실행 중이어도 이후 기록은 새 파일로 이어집니다."
        confirmLabel="지우기"
        onCancel={() => setConfirmClear(false)}
        onConfirm={async () => {
          setConfirmClear(false);
          if (await run("clear_activity_log", "로그를 지우지 못했습니다")) {
            setSelectedId(null);
            setDetailOpen(false);
            void log.reload();
          }
        }}
      />
      <LogSettingsDialog open={settingsOpen} onClose={() => setSettingsOpen(false)} onSaved={() => void log.reload()} />
    </div>
  );
}
